'use client'

import { ProductCardAnimationProvider } from 'product-card/context/ProductCardAnimationContext'
import { ProductCardAnimation } from 'product-card/types'
import {
	useProductList,
	useProductListSlideshowAnimationRendered,
	useProductListView,
} from 'product-list/hooks/useProductList/useProductList'
import { View } from 'product-list/types/View'
import { getNavigationType } from 'product-list/utils/getNavigationType'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { every } from 'utils/every/every'
import { some } from 'utils/some/some'

type Props = {
	readonly children: ReactNode
}

export const AddProductCardAnimation = ({ children }: Props) => {
	const { isSmall, isTouchable, isLargeOrGreater } = useResponsive()

	const view = useProductListView()
	const isSlideshowAnimationRendered =
		useProductListSlideshowAnimationRendered()
	const { shouldRenderSlideshowAnimation, setSlideshowAnimationRendered } =
		useProductList()

	const canRenderSlideshowAnimation = every(
		shouldRenderSlideshowAnimation,
		!isSlideshowAnimationRendered,
		view !== View.Overview,
		isTouchable
	)
	const canRenderShortAnimation = some(
		view === View.Inspirational && isSmall,
		view === View.Standard && isLargeOrGreater
	)

	const [animation, setAnimation] = useState(ProductCardAnimation.FadeIn)

	const handleAnimationEnd = useCallback(() => {
		setSlideshowAnimationRendered(true)
	}, [setSlideshowAnimationRendered])

	useEffect(() => {
		if (!canRenderSlideshowAnimation) {
			setAnimation(ProductCardAnimation.FadeIn)
			return
		}

		// If the page is not a navigation, we don't need to render the slideshow animation
		const navType = getNavigationType()
		if (navType !== 'navigate') {
			setAnimation(ProductCardAnimation.FadeIn)
			return
		}

		if (canRenderShortAnimation) {
			setAnimation(ProductCardAnimation.ShortSlideshow)
		} else {
			setAnimation(ProductCardAnimation.LongSlideshow)
		}
	}, [canRenderShortAnimation, canRenderSlideshowAnimation])

	return (
		<ProductCardAnimationProvider
			animation={animation}
			onAnimationEnd={handleAnimationEnd}
		>
			{children}
		</ProductCardAnimationProvider>
	)
}
